import React, { useState } from 'react';
import type { QuestId } from '@app/lib/site';
import styles from './style.module.css';

// One curated shot per quest: the real location with the app open on a phone
// (question and answers blurred). Source files: algarve-quests/mockups/.
const mockupSrc = (quest: QuestId) => `/img/mockups/${quest}.jpg`;

interface Props {
  quest: QuestId;
  alt: string;
  /** Slider aspect ratio. */
  w: number;
  h: number;
  rounded?: boolean;
  /** Optional extra slide (the interactive route map), shown after the photo. */
  mapNode?: React.ReactNode;
}

export const QuestGallery: React.FC<Props> = ({ quest, alt, w, h, rounded, mapNode }) => {
  const photos = [mockupSrc(quest)];
  const [i, setI] = useState(0);

  const mapIndex = mapNode ? photos.length : -1;
  const n = photos.length + (mapNode ? 1 : 0);
  const go = (d: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setI((prev) => (prev + d + n) % n);
  };

  return (
    <div className={`${styles.gallery} ${rounded ? styles.rounded : ''}`}>
      <div className={styles.viewport} style={{ aspectRatio: `${w} / ${h}` }}>
        {photos.map((src, k) => (
          <img
            key={src + k}
            src={src}
            alt={alt}
            width={1500}
            height={1000}
            loading={k === 0 ? 'eager' : 'lazy'}
            className={`${styles.img} ${k === i ? styles.active : ''}`}
          />
        ))}
        {mapNode && (
          <div className={`${styles.slide} ${styles.mapSlide} ${i === mapIndex ? styles.active : ''}`}>
            {mapNode}
          </div>
        )}
      </div>
      {n > 1 && (
        <>
          <button type="button" aria-label="Previous photo" className={`${styles.nav} ${styles.prev}`} onClick={(e) => go(-1, e)}>
            ‹
          </button>
          <button type="button" aria-label="Next photo" className={`${styles.nav} ${styles.next}`} onClick={(e) => go(1, e)}>
            ›
          </button>
          <div className={styles.dots}>
            {Array.from({ length: n }).map((_, k) => (
              <button
                key={k}
                type="button"
                aria-label={k === mapIndex ? 'Route map' : `Photo ${k + 1}`}
                className={`${styles.dot} ${k === mapIndex ? styles.dotMap : ''} ${k === i ? styles.dotActive : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setI(k);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default QuestGallery;
